import { SeoLandingLayout, SeoLandingPageData } from '../../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Subvention en retard : comment protéger votre trésorerie | Trezoone',
    description: 'La subvention est attendue mais pas versée. Comment gérer le retard, relancer le financeur et sécuriser votre trésorerie dans l\'attente.',
    canonical: '/subvention-en-retard-tresorerie',
    keywords: 'subvention en retard, subvention retardée trésorerie, retard versement subvention, attente subvention trésorerie association',
  },
  hero: {
    tag: 'Subvention en retard',
    title: 'La subvention devait arriver en mars. On est en mai. Votre compte plonge.',
    subtitle: 'C\'est l\'une des situations les plus courantes dans les associations et structures aidées. Voici comment l\'anticiper — et comment réagir quand c\'est déjà arrivé.',
    ctaLabel: 'Simuler l\'impact du retard',
    ctaHref: '/simulateur-tresorerie-association',
  },
  pilierLink: { label: 'Guide trésorerie association', href: '/gestion-tresorerie-association' },
  sections: [
    {
      type: 'hook',
      title: 'Pourquoi les subventions arrivent toujours en retard',
      content: 'Ce n\'est pas une exception. C\'est la norme. Les délais administratifs sont structurellement longs :',
      items: [
        'Instruction de la demande : 2 à 4 mois après dépôt du dossier',
        'Vote en conseil municipal, régional ou départemental : délais imprévisibles',
        'Ordonnancement comptable : encore 4 à 8 semaines après la décision',
        'Résultat : une subvention notifiée en décembre peut arriver en avril ou mai',
      ],
    },
    {
      type: 'explain',
      title: 'Comment calculer l\'impact d\'un retard de subvention sur votre trésorerie',
      content: 'Pour mesurer l\'impact, il faut deux chiffres : le montant de la subvention et vos charges mensuelles.',
      items: [
        'Subvention attendue : 20 000 € en mars',
        'Charges mensuelles : 8 000 €',
        'Solde actuel : 5 000 €',
        'Si la subvention arrive avec 2 mois de retard : solde mai = 5 000 - (8 000 × 2) + 0 = -11 000 €',
        'Vous avez besoin de 11 000 € de financement relais pour ces 2 mois',
        'Trezoone simule ce scénario en 2 clics',
      ],
    },
    {
      type: 'example',
      title: 'Ce que vous pouvez faire concrètement',
      content: 'Vous savez qu\'il y a un risque de retard. Voici les actions par ordre de priorité :',
      items: [
        '1. Relancer le financeur par écrit dès J+15 après la date prévue — créez une trace',
        '2. Demander une avance sur subvention (possible avec certains financeurs publics)',
        '3. Négocier un découvert autorisé avec votre banque AVANT d\'en avoir besoin',
        '4. Décaler certaines dépenses non urgentes pour préserver le cash',
        '5. Activer un fonds de garantie ou un prêt associatif si le retard s\'annonce long',
      ],
    },
    {
      type: 'midcta',
      title: 'Subvention attendue non versée ? Simulez l\'impact sur votre compte en 2 clics',
      content: 'Trezoone alerte automatiquement dès qu\'une subvention n\'arrive pas à la date prévue — et simule l\'impact du retard sur vos 3 prochains mois.',
    },
    {
      type: 'solution',
      title: 'Trezoone vous alerte avant que le retard devienne critique',
      content: 'Configurez Trezoone avec la date de versement prévue de chaque subvention. Si elle n\'arrive pas, une alerte est envoyée — avant que votre solde soit dans le rouge.',
      items: [
        'Suivi de chaque subvention : montant, financeur, date prévue, état',
        'Alerte automatique si une subvention attendue n\'est pas reçue à la date prévue',
        'Simulation immédiate de l\'impact d\'un retard sur votre solde des 3 prochains mois',
        'Rapport exportable pour justifier une demande d\'avance ou de découvert',
      ],
    },
    {
      type: 'cluster',
      title: 'Ressources pour associations dépendantes de subventions',
      links: [
        { label: 'Simulateur trésorerie association', href: '/simulateur-tresorerie-association' },
        { label: 'Logiciel trésorerie association', href: '/logiciel-tresorerie-association' },
        { label: 'Trésorerie association loi 1901', href: '/tresorerie-association-loi-1901' },
        { label: 'Reporting financier association', href: '/reporting-financier-association' },
        { label: 'Simulation trésorerie association', href: '/simulation-tresorerie-association' },
        { label: 'Alerte trésorerie', href: '/alerte-tresorerie' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
      ],
    },
    {
      type: 'cta',
      title: 'Ne découvrez plus les retards de subvention au dernier moment',
      content: 'Configurez vos subventions dans Trezoone et soyez alerté à temps pour agir.',
    },
  ],
  relatedLinks: [
    { label: 'Simulateur trésorerie association', href: '/simulateur-tresorerie-association' },
    { label: 'Logiciel trésorerie association', href: '/logiciel-tresorerie-association' },
    { label: 'Trésorerie association loi 1901', href: '/tresorerie-association-loi-1901' },
    { label: 'Reporting financier association', href: '/reporting-financier-association' },
    { label: 'Simulation trésorerie association', href: '/simulation-tresorerie-association' },
    { label: 'Alerte trésorerie', href: '/alerte-tresorerie' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }
export default function SubventionRetardTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
